import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { consentCategories, CONSENT_OPEN_PREFERENCES_EVENT } from "../../../consent/config";
import { useConsent } from "../../../consent/ConsentContext";
import { COLORS } from "../../../values/colors";
import {
  bannerChipSx,
  bannerDescriptionSx,
  bannerHeadlineSx,
  bannerPaperSx,
  bannerTitleSx,
  bannerWrapperSx,
  buttonSx,
  categoryCardSx,
  categoryDescriptionSx,
  categoryMetaSx,
  categoryTextWrapperSx,
  categoryTitleSx,
  categoryToggleSx,
  dialogActionsSx,
  dialogContentSx,
  dialogHeadlineSx,
  dialogPaperSx,
  dialogTitleSx,
  introTextSx,
} from "./styles";

const CookieConsentManager = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    bannerVisible,
    settingsOpen,
    preferences,
    setCategory,
    acceptAll,
    rejectAll,
    saveCurrentSelection,
    closeSettings,
  } = useConsent();

  const openSettings = () => {
    window.dispatchEvent(new CustomEvent(CONSENT_OPEN_PREFERENCES_EVENT));
  };

  return (
    <>
      {bannerVisible ? (
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
          sx={bannerWrapperSx}
        >
          <Paper elevation={24} sx={bannerPaperSx}>
            <Stack spacing={1.2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.2}
                justifyContent="space-between"
              >
                <Box>
                  <Typography id="cookie-banner-title" variant="overline" sx={bannerTitleSx}>
                    Cookie-Einstellungen
                  </Typography>
                  <Typography variant="h5" sx={bannerHeadlineSx}>
                    Deine Einwilligung für Cookies und ähnliche Technologien
                  </Typography>
                </Box>
                <Chip
                  label="Nicht essenzielle Kategorien standardmäßig aus"
                  sx={bannerChipSx}
                />
              </Stack>
              <Typography id="cookie-banner-description" sx={bannerDescriptionSx}>
                Wir verwenden essenzielle Technologien für den technischen Betrieb.
                Statistik, Marketing, externe Medien und Komfortfunktionen aktivieren
                wir erst nach deiner ausdrücklichen Einwilligung. Du kannst alle
                optionalen Kategorien genauso einfach ablehnen wie akzeptieren und
                deine Auswahl später jederzeit über den Link "Cookie-Einstellungen"
                ändern oder widerrufen.
              </Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <Button
                  type="button"
                  variant="contained"
                  fullWidth={isMobile}
                  onClick={acceptAll}
                  sx={{
                    ...buttonSx,
                    background: `linear-gradient(135deg, ${COLORS.apronBrown}, ${COLORS.apronBrownDark})`,
                  }}
                >
                  Alle akzeptieren
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  fullWidth={isMobile}
                  onClick={rejectAll}
                  sx={{
                    ...buttonSx,
                    color: COLORS.white,
                    borderColor: "rgba(255,255,255,0.24)",
                  }}
                >
                  Alle ablehnen
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  fullWidth={isMobile}
                  onClick={saveCurrentSelection}
                  sx={{
                    ...buttonSx,
                    color: COLORS.white,
                    borderColor: "rgba(181,115,76,0.24)",
                  }}
                >
                  Auswahl speichern
                </Button>
                <Button
                  type="button"
                  variant="text"
                  fullWidth={isMobile}
                  onClick={openSettings}
                  sx={{ ...buttonSx, color: COLORS.creamWhite }}
                >
                  Einstellungen
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      ) : null}

      <Dialog
        open={settingsOpen}
        onClose={closeSettings}
        fullWidth
        fullScreen={isMobile}
        maxWidth="md"
        aria-labelledby="cookie-settings-title"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle id="cookie-settings-title" sx={dialogTitleSx}>
          <Typography variant="overline" sx={{ color: COLORS.apronBrown, letterSpacing: "0.18em" }}>
            Cookie-Einstellungen
          </Typography>
          <Typography variant="h5" sx={dialogHeadlineSx}>
            Einwilligungen verwalten
          </Typography>
        </DialogTitle>
        <DialogContent sx={dialogContentSx}>
          <Stack spacing={1.4} sx={{ pt: 2 }}>
            <Typography sx={introTextSx}>
              Essenzielle Funktionen bleiben aktiv, soweit sie technisch unbedingt
              erforderlich sind. Alle anderen Kategorien sind freiwillig und
              standardmäßig deaktiviert. Deine Ablehnung ist genauso einfach wie
              deine Zustimmung.
            </Typography>
            {consentCategories.map((category) => (
              <Paper key={category.key} elevation={0} sx={categoryCardSx}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.2}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <Box sx={categoryTextWrapperSx}>
                    <Typography sx={categoryTitleSx}>{category.title}</Typography>
                    <Typography sx={categoryDescriptionSx}>{category.description}</Typography>
                    <Typography variant="body2" sx={categoryMetaSx}>
                      {category.requiresConsent
                        ? "Einwilligung erforderlich"
                        : "Keine Einwilligung erforderlich, soweit unbedingt notwendig"}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences[category.key]}
                        onChange={(event) => setCategory(category.key, event.target.checked)}
                        disabled={category.alwaysActive}
                        inputProps={{
                          "aria-label": `${category.title} aktivieren oder deaktivieren`,
                        }}
                      />
                    }
                    label={
                      category.alwaysActive
                        ? "Immer aktiv"
                        : preferences[category.key]
                          ? "Aktiv"
                          : "Deaktiviert"
                    }
                    labelPlacement="start"
                    sx={categoryToggleSx}
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button
            type="button"
            variant="contained"
            fullWidth={isMobile}
            onClick={acceptAll}
            sx={{
              ...buttonSx,
              background: `linear-gradient(135deg, ${COLORS.apronBrown}, ${COLORS.apronBrownDark})`,
            }}
          >
            Alle akzeptieren
          </Button>
          <Button
            type="button"
            variant="outlined"
            fullWidth={isMobile}
            onClick={rejectAll}
            sx={{
              ...buttonSx,
              color: COLORS.darkGray,
              borderColor: "rgba(0,0,0,0.24)",
            }}
          >
            Alle ablehnen
          </Button>
          <Button
            type="button"
            variant="outlined"
            fullWidth={isMobile}
            onClick={saveCurrentSelection}
            sx={{
              ...buttonSx,
              color: COLORS.darkGray,
              borderColor: "rgba(181,115,76,0.3)",
            }}
          >
            Auswahl speichern
          </Button>
          <Button
            type="button"
            variant="text"
            fullWidth={isMobile}
            onClick={closeSettings}
            sx={{ ...buttonSx, color: COLORS.darkGray }}
          >
            Schliessen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsentManager;
